import type {
	AliasStatus,
	CheckState,
	DnsRecordStatus,
	DomainStatus,
	InvoiceStatus,
	MailboxProvisioningState,
	MailboxStatus,
	MigrationStatus,
	OrganisationRole,
	SubscriptionStatus,
	SupportCategory,
	SupportStatus,
} from "@bafe/types";
import type { StatusTone } from "@bafe/ui";

export interface StatusDescriptor {
	tone: StatusTone;
	label: string;
}

export const CHECK_STATE: Record<CheckState, StatusDescriptor> = {
	pass: { tone: "positive", label: "Working" },
	warn: { tone: "caution", label: "Needs attention" },
	fail: { tone: "critical", label: "Not working" },
	unknown: { tone: "pending", label: "Not checked yet" },
};

export const DOMAIN_STATUS: Record<DomainStatus, StatusDescriptor> = {
	pending: { tone: "pending", label: "Setup not finished" },
	verifying: { tone: "pending", label: "Checking your DNS" },
	connected: { tone: "positive", label: "Connected" },
	degraded: { tone: "caution", label: "Needs attention" },
	suspended: { tone: "critical", label: "Suspended" },
	removing: { tone: "neutral", label: "Being removed" },
	removed: { tone: "neutral", label: "Removed" },
};

export const DNS_RECORD_STATUS: Record<DnsRecordStatus, StatusDescriptor> = {
	pending: { tone: "pending", label: "Pending" },
	detected: { tone: "positive", label: "Detected" },
	incorrect: { tone: "caution", label: "Incorrect" },
	conflict: { tone: "critical", label: "Conflict" },
	optional: { tone: "info", label: "Optional" },
};

export const MAILBOX_STATUS: Record<MailboxStatus, StatusDescriptor> = {
	provisioning: { tone: "pending", label: "Being created" },
	active: { tone: "positive", label: "Active" },
	suspended: { tone: "caution", label: "Suspended" },
	over_quota: { tone: "caution", label: "Over its storage limit" },
	scheduled_for_deletion: { tone: "critical", label: "Scheduled for deletion" },
	deleting: { tone: "neutral", label: "Being deleted" },
	deleted: { tone: "neutral", label: "Deleted" },
};

/** Status pill for a mailbox row. Prefer engine readiness over a stale status column. */
export function mailboxDisplayStatus(mailbox: {
	status: MailboxStatus;
	provisioningState: MailboxProvisioningState;
}): MailboxStatus {
	if (
		mailbox.status === "scheduled_for_deletion" ||
		mailbox.status === "deleting" ||
		mailbox.status === "suspended" ||
		mailbox.status === "over_quota"
	) {
		return mailbox.status;
	}
	if (mailbox.provisioningState === "ready") return "active";
	return mailbox.status;
}

export function mailboxStatusDescriptor(mailbox: {
	status: MailboxStatus;
	provisioningState: MailboxProvisioningState;
}): StatusDescriptor {
	return MAILBOX_STATUS[mailboxDisplayStatus(mailbox)];
}

export const ALIAS_STATUS: Record<AliasStatus, StatusDescriptor> = {
	provisioning: { tone: "pending", label: "Being created" },
	active: { tone: "positive", label: "Active" },
	suspended: { tone: "caution", label: "Suspended" },
	removing: { tone: "neutral", label: "Being removed" },
	removed: { tone: "neutral", label: "Removed" },
};

export const MIGRATION_STATUS: Record<MigrationStatus, StatusDescriptor> = {
	draft: { tone: "neutral", label: "Not started" },
	checking: { tone: "pending", label: "Checking the connection" },
	ready: { tone: "info", label: "Ready to start" },
	queued: { tone: "pending", label: "Queued" },
	migrating: { tone: "pending", label: "Copying messages" },
	retrying: { tone: "caution", label: "Retrying" },
	completed: { tone: "positive", label: "Finished" },
	completed_with_errors: { tone: "caution", label: "Finished, some messages skipped" },
	failed: { tone: "critical", label: "Could not finish" },
	cancelled: { tone: "neutral", label: "Cancelled" },
};

export function describeMigrationStatus(migration: {
	status: MigrationStatus;
	startedAt: string | null;
	processedMessages: number;
}): StatusDescriptor {
	if (migration.status === "ready" && (migration.startedAt || migration.processedMessages > 0)) {
		return { tone: "caution", label: "Paused" };
	}
	return MIGRATION_STATUS[migration.status];
}

export function migrationPercent(migration: {
	status: MigrationStatus;
	progress: number | null;
	totalMessages: number;
	processedMessages: number;
}): number | null {
	if (migration.progress !== null) return Math.round(migration.progress * 100);
	if (migration.totalMessages > 0) {
		return Math.round((migration.processedMessages / migration.totalMessages) * 100);
	}
	if (migration.status === "completed") return 100;
	if (migration.status === "draft" || migration.status === "ready") return 0;
	return null;
}

export const INVOICE_STATUS: Record<InvoiceStatus, StatusDescriptor> = {
	draft: { tone: "neutral", label: "Draft" },
	open: { tone: "caution", label: "Due" },
	paid: { tone: "positive", label: "Paid" },
	void: { tone: "neutral", label: "Void" },
	uncollectible: { tone: "critical", label: "Unpaid" },
};

export const SUPPORT_STATUS: Record<SupportStatus, StatusDescriptor> = {
	open: { tone: "info", label: "Open" },
	pending_customer: { tone: "caution", label: "Waiting for you" },
	pending_bafe: { tone: "pending", label: "With Key" },
	resolved: { tone: "positive", label: "Resolved" },
	closed: { tone: "neutral", label: "Closed" },
};

export interface SubscriptionStateCopy extends StatusDescriptor {
	/** Accurate and calm: a payment problem never destroys mail. */
	explanation: string;
}

export const SUBSCRIPTION_STATUS: Record<SubscriptionStatus, SubscriptionStateCopy> = {
	trialing: {
		tone: "info",
		label: "Trial",
		explanation: "You are on a trial. Add a payment method before it ends to keep your email running.",
	},
	active: {
		tone: "positive",
		label: "Active",
		explanation: "Your plan is active and renews automatically.",
	},
	past_due: {
		tone: "caution",
		label: "Payment failed",
		explanation:
			"We could not take the last payment. Your email keeps sending and receiving while we retry. Update your payment method to clear this.",
	},
	grace_period: {
		tone: "caution",
		label: "Grace period",
		explanation:
			"Your payment is still outstanding. Email continues to work during the grace period, and nothing has been deleted.",
	},
	restricted: {
		tone: "caution",
		label: "Restricted",
		explanation:
			"New mailboxes and domains are paused until payment is settled. Existing mail is untouched and still delivered.",
	},
	suspended: {
		tone: "critical",
		label: "Suspended",
		explanation:
			"Mail access is paused while the account is unpaid. Your messages are still stored. Settling payment restores access.",
	},
	cancelled: {
		tone: "neutral",
		label: "Cancelled",
		explanation: "This plan will not renew. Your mail stays available until the end of the paid period.",
	},
	expired: {
		tone: "neutral",
		label: "Expired",
		explanation: "This plan has ended. Reactivate to start sending and receiving again.",
	},
};

export const ROLE_LABEL: Record<OrganisationRole, string> = {
	owner: "Owner",
	admin: "Administrator",
	user: "User",
	billing: "Billing",
	support_viewer: "Support viewer",
};

export const ROLE_DESCRIPTION: Record<OrganisationRole, string> = {
	owner: "Full access, including billing and ownership transfer.",
	admin: "Manages domains, email accounts and aliases. No ownership transfer.",
	user: "Opens Key Mail for inboxes shared with them, and My Tasks in Hub.",
	billing: "Sees the plan, invoices and payment method only.",
	support_viewer: "Read-only access for someone helping you troubleshoot.",
};

export const SUPPORT_CATEGORY_LABEL: Record<SupportCategory, string> = {
	domain_setup: "Domain setup",
	sending: "Sending",
	receiving: "Receiving",
	mailbox: "Mailbox",
	migration: "Migration",
	billing: "Billing",
	security: "Security",
	other: "Other",
};
